import { RegistrationScreen } from '@/components/utils/RegistrationScreen';
import useUserConsentController from '@/hooks/registration/useUserConsentController';
import { useForm } from 'react-hook-form';
import { useState, useEffect } from 'react';
import { ConsentCheckbox } from '@/components/screens/registration/consent/ConsentCheckbox';
import { ConsentRadioButton } from '@/components/screens/registration/consent/ConsentRadioButton';
import useRegisterStore, { RegistrationTrack } from '@/hooks/useRegistrationStore';
import { RegistrationSuccess } from '@/components/screens/registration/RegistrationSuccess';
import { isHtmlContent } from '@/utils/consentUtils';
import { useNavigate } from 'react-router-dom';
import { REGISTRATION_CONFIRMATION } from '@/constants/paths';
import { Sentry } from '@/main';

const UserConsentScreen = () => {
  const navigate = useNavigate();
  const [checkboxValues, setCheckboxValues] = useState<Record<number, string>>({});
  const [isFormValid, setIsFormValid] = useState(false);
  const [radioErrorMessage, setRadioErrorMessage] = useState<string | null>(null);
  const [nextDisabled, setNextDisabled] = useState(true);

  const {
    isLoading,
    form,
    consentData,
    consentFormTitle,
    control,
    isLastStep,
    isRadioButton,
    isCheckbox,
    onClickGoBack,
    onClickNext,
    onDownloadConsentPress,
    isPendingRegistration,
  } = useUserConsentController(() => checkboxValues);
  const { registrationDone, ...registrationData } = useRegisterStore();

  const loadingForm = useForm<any>();
  const watchedRadioValue = form.watch('consentChoice');

  useEffect(() => {
    if (!isLoading && !consentData) {
      const error = new Error('Oops! Error Fetching terms and services data.');
      Sentry.captureException(error, {
        extra: {
          ...registrationData,
        },
      });
    }
  }, [isLoading, consentData, registrationData]);

  useEffect(() => {
    let isNextDisabled = isPendingRegistration;
    if (isRadioButton) {
      // For radio buttons: disable if no selection made OR if there's an error message
      const hasSelection = watchedRadioValue !== undefined && watchedRadioValue !== null;
      isNextDisabled = !hasSelection || !!radioErrorMessage || isPendingRegistration;
    } else if (isCheckbox) {
      isNextDisabled = isFormValid || !!radioErrorMessage;
    }
    setNextDisabled(isNextDisabled);
  }, [ isPendingRegistration, isFormValid, radioErrorMessage, isRadioButton, isCheckbox, watchedRadioValue ]);

  useEffect(() => {
    if (consentData && isCheckbox) {
      const initialValues = consentData.choices?.reduce<Record<number, string>>((acc, choice) => {
        acc[choice.ordinal] = "false";
        return acc;
      }, {}) || {};
      setCheckboxValues(initialValues);
    }
  }, [consentData, isCheckbox]);

  if (isLoading || !consentData) {
    return (
      <RegistrationScreen
        title="User Consent"
        form={loadingForm}
        content={<div data-testid="registrationUserConsent-error">Oops! Error Fetching terms and services data.</div>}
        primaryActionLabel="Try Again"
        primaryActionOnClick={() => navigate(REGISTRATION_CONFIRMATION)}
        checkForRegisterStore={RegistrationTrack.ConsentForm}
        data-testid="registrationUserConsent-loadingScreen"
      />
    );
  }

  const { choices, header, content } = consentData;

  const handleNextClick = () => {
    onClickNext();
  };

  const Content = (
    <div data-testid="registrationUserConsent-content">
      {header && (
        <h2 className="text-4xl font-sans mb-4 font-medium" data-testid="registrationUserConsent-header">
          {header}
        </h2>
      )}
      {isHtmlContent(content) ? (
        content.length > 500 ? (
          <div className="flex flex-col items-center w-full" data-testid="registrationUserConsent-longContent">
            {content.includes('Introduction') && (
              <div className="w-full mb-4" data-testid="registrationUserConsent-intro">
                <div
                  className="prose max-w-none [&>h1]:typography-d3"
                  dangerouslySetInnerHTML={{
                    __html: content.split('Introduction')[0],
                  }}
                />
              </div>
            )}

            <div className="w-full bg-white rounded-xl shadow-sm p-4" data-testid="registrationUserConsent-scrollableContent">
              <div className="max-h-[44vh] overflow-y-auto">
                <div className="p-6 typography-body text-secondary">
                  <div
                    className="prose prose-sm max-w-none"
                    dangerouslySetInnerHTML={{
                      __html: content.includes('Introduction')
                        ? 'Introduction' + content.split('Introduction')[1]
                        : content,
                    }}
                  />
                </div>
              </div>
            </div>
          </div>
        ) : (
          <div
            className="mt-4 prose max-w-none"
            dangerouslySetInnerHTML={{ __html: content }}
            data-testid="registrationUserConsent-shortContent"
          />
        )
      ) : (
        <div className="mt-4 prose" data-testid="registrationUserConsent-textContent">
          <p>{content}</p>
        </div>
      )}
      {consentData?.downloadLink && (
        <p 
          onClick={onDownloadConsentPress} 
          className='text-sm font-medium leading-none underline text-gray-600 mt-4 cursor-pointer'
          data-testid="registrationUserConsent-downloadLink"
        >
          Download this Consent Form
        </p>
      )}
      {isCheckbox && (
        <ConsentCheckbox
          choices={choices || []}
          values={checkboxValues}
          onChange={setCheckboxValues}
          onValidationChange={setIsFormValid}
          consentSystemId={consentData?.systemId}
        />
      )}
      {isRadioButton && (
        <ConsentRadioButton
          choices={choices || []}
          control={control}
          errorMessage={radioErrorMessage}
          onErrorChange={setRadioErrorMessage}
          onValidationChange={setIsFormValid}
          watchValue={form.watch('consentChoice')}
        />
      )}
    </div>
  );

  return registrationDone ? <RegistrationSuccess /> : (
    <RegistrationScreen
      title={consentFormTitle}
      form={form as any}
      content={Content}
      previousPath={REGISTRATION_CONFIRMATION}
      primaryActionIsSubmit={false}
      primaryActionLabel={isLastStep ? 'Register' : 'Next'}
      primaryActionOnClick={handleNextClick}
      primartActionDisabled={nextDisabled}
      isLoading={isPendingRegistration}
      secondaryActionLabel="Back"
      secondaryActionOnClick={onClickGoBack}
      isConsentScreen
      checkForRegisterStore={RegistrationTrack.ConsentForm}
      data-testid="registrationUserConsent-screen"
    />
  );
};

export default UserConsentScreen; 